import { useEffect, useState } from 'react';
import { Autocomplete, Box, Modal, Stack, TextField } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { CountryOption, ProductIdentityValues } from 'services/lookupsService';
import { RawProductInfo, RawProductInfoPatch } from 'services/rawProductService';

export type InfoFieldName = keyof RawProductInfo;

// Free text, a number, one of a short list, or a country. Which one a field is
// decides the control it gets, and getting it wrong is how a weight ends up
// stored as the string a picker showed.
type InfoFieldKind = 'text' | 'number' | 'unit' | 'country';

export const INFO_FIELDS: Record<
  InfoFieldName,
  { label: string; hint: string; kind: InfoFieldKind }
> = {
  nameEn: {
    label: 'Name (EN)',
    hint: 'What a buyer would say to ask for this pack rather than the one beside it: the brand, what it is, and the variant that tells it apart.',
    kind: 'text',
  },
  nameAr: {
    label: 'Name (AR)',
    hint: "Where the pack prints its own Arabic name, that name - not a translation of the English one.",
    kind: 'text',
  },
  weight: {
    label: 'Weight',
    hint: 'The net contents as printed on the pack. Leave it empty to say the pack states none.',
    kind: 'number',
  },
  drainedWeight: {
    label: 'Drained weight',
    hint: 'Only where the pack states one separately from the net contents.',
    kind: 'number',
  },
  estimatedWeight: {
    label: 'Estimated weight',
    hint: 'Judged from the photo, and only used when the pack states no weight at all. Kept apart from the weight so a judgement is never mistaken for something printed.',
    kind: 'number',
  },
  weightUnit: {
    label: 'Weight unit',
    hint: 'The unit all three numbers above are in.',
    kind: 'unit',
  },
  countryOfManufactureISO: {
    label: 'Made in',
    hint: 'Only where the pack states where the product was made. A company address or a brand home country is not that.',
    kind: 'country',
  },
  countryOfRegistrationISO: {
    label: 'Registered in',
    hint: "Taken from the barcode's own prefix, so it is usually right without anyone reading the pack.",
    kind: 'country',
  },
  price: {
    label: 'Price',
    hint: 'What the suggestion that brought this product in was priced at.',
    kind: 'number',
  },
};

interface Props {
  // Null when nothing is being edited on this card.
  field: InfoFieldName | null;
  productName: string;
  info: RawProductInfo;
  identityValues?: ProductIdentityValues;
  countries?: CountryOption[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (patch: RawProductInfoPatch) => void;
}

export default function InfoFieldModal({
  field,
  productName,
  info,
  identityValues,
  countries,
  isSaving,
  onClose,
  onSave,
}: Props) {
  // Held as the text the reviewer typed rather than as a number, so a half-typed
  // value is not silently rounded or blanked while they are still typing it.
  const [draft, setDraft] = useState('');

  const config = field ? INFO_FIELDS[field] : null;
  const stored = field ? info[field] : null;
  const storedText = stored === null || stored === undefined ? '' : String(stored);

  useEffect(() => {
    if (!field) return;
    setDraft(storedText);
  }, [field, storedText]);

  const trimmed = draft.trim();
  const isNumberField = config?.kind === 'number';
  // An empty box is null - the pack states none - which is a real answer and not
  // an unfinished edit.
  const parsed: string | number | null = !trimmed
    ? null
    : isNumberField
      ? Number(trimmed)
      : trimmed;
  const invalid = isNumberField && parsed !== null && !Number.isFinite(parsed as number);
  const changed = trimmed !== storedText.trim();

  const countryLabel = (iso: string) => {
    const country = countries?.find((option) => option.iso === iso);
    return country ? `${country.name} (${country.iso})` : iso;
  };

  return (
    <Modal open={Boolean(field)} onClose={isSaving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 440,
          maxWidth: '90vw',
        }}
      >
        <CustomText
          text={config ? `Edit ${config.label.toLowerCase()}` : ''}
          fontWeight={700}
          fontSize={18}
        />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        <Box mt={2}>
          {config?.kind === 'unit' && (
            <Autocomplete
              size="small"
              options={identityValues?.weightUnits ?? []}
              value={draft || null}
              disabled={isSaving}
              onChange={(_event, next) => setDraft(next ?? '')}
              renderInput={(params) => <TextField {...params} placeholder="—" autoFocus />}
            />
          )}

          {config?.kind === 'country' && (
            <Autocomplete
              size="small"
              options={(countries ?? []).map((country) => country.iso)}
              getOptionLabel={countryLabel}
              value={draft || null}
              disabled={isSaving}
              onChange={(_event, next) => setDraft(next ?? '')}
              renderInput={(params) => <TextField {...params} placeholder="—" autoFocus />}
            />
          )}

          {(config?.kind === 'text' || config?.kind === 'number') && (
            <TextField
              fullWidth
              size="small"
              autoFocus
              multiline={config.kind === 'text'}
              type={config.kind === 'number' ? 'number' : 'text'}
              value={draft}
              disabled={isSaving}
              error={invalid}
              helperText={invalid ? 'That is not a number.' : undefined}
              placeholder="—"
              onChange={(event) => setDraft(event.target.value)}
            />
          )}
        </Box>

        {config && (
          <Box mt={1}>
            <CustomText text={config.hint} fontSize={11} color={colors.lightgray} />
          </Box>
        )}

        {/* These are not part of the category decision, so saying what they are
            for belongs here rather than on the card: they are what a product is
            built from the moment one is created. */}
        <Box mt={1}>
          <CustomText
            text="Saved to the product record this row will become. It changes no category verdict."
            fontSize={11}
            color={colors.lightgray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={isSaving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={isSaving}
            disabled={!changed || invalid}
            onClick={() => {
              if (!field) return;
              onSave({ [field]: parsed } as RawProductInfoPatch);
            }}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
